export const shelf = {
  title: "My Shelf",
  pageTitle: "My Shelf | GlobalComix",
  tabs: {
    library: "Library",
    purchases: "Purchases",
    creators: "Creators",
    readingLists: "Lists",
    monthlyRewards: "Member Rewards",
  },
  creatorsSearchPlaceholder: "Search creators...",
  readingLists: {
    layout: {
      searchPlaceholder: "Search lists",
      searchClearAriaLabel: "Clear search",
      allTab: "All",
      yoursTab: "My lists",
      followingTab: "Following",
      newButton: "New list",
      createListButton: "Create list",
      createListTile: "Create a list",
      followButton: "Follow",
      followingButton: "Following",
      backToLists: "Back to lists",
      emptyDetail: "Pick a list to see it here.",
      noResults: "No lists match this filter.",
      noOwnLists: "You haven't created any lists yet.",
      ownedBadge: "Yours",
      seriesBadge: "Series",
      chaptersBadge: "Chapters",
      // Top-right rail-card pill — only renders for owner-visible private
      // lists. Non-owners never see private lists in the rail (the backend
      // filters them out), so the badge is effectively a "yours, hidden"
      // marker for the user's own collection.
      privateBadge: "Private",
      sortCreatedTime: "Created",
      sortUpdateTime: "Last updated",
      sortAlphabetical: "Alphabetical",
      sortAriaLabel: "Sort lists",
    },
    error: {
      title: "Couldn't load your lists.",
      retry: "Try refreshing the page.",
    },
    menu: {
      ariaLabel: "List options",
      edit: "Edit",
      unfollow: "Unfollow",
      share: "Share",
      shareTitle: "Share list",
      delete: "Delete",
    },
    deleteDialog: {
      title: "Delete list",
      body: 'Delete "{{name}}"? This can\'t be undone.',
      confirm: "Delete",
    },
    toasts: {
      deleted: "List deleted",
      deleteError: "Couldn't delete list",
      unfollowed: "Unfollowed",
      unfollowError: "Couldn't unfollow",
    },
    comicCount_one: "{{count}} comic",
    comicCount_other: "{{count}} comics",
    releaseCount_one: "{{count}} release",
    releaseCount_other: "{{count}} releases",
    viewCount_one: "{{formattedCount}} view",
    viewCount_other: "{{formattedCount}} views",
    createdAt: "Created {{date}}",
    cover: {
      uploadHint: "Upload cover",
      uploadConfirm: "Upload cover",
      cropTitle: "Crop cover image",
      cancel: "Cancel",
      replaceAria: "Replace cover",
      removeAria: "Remove cover",
    },
    coverToasts: {
      uploaded: "Cover updated",
      uploadError: "Couldn't upload cover",
      removed: "Cover removed",
      removeError: "Couldn't remove cover",
    },
    placeholder: {
      editTitle: "Reading list editor",
      editBody: "Editor ships in GX-3644.",
      viewTitle: "Reading list",
      viewBody: "Public view ships in GX-3639.",
      viewBodyWithId: "List {{listId}} — public view ships in GX-3639.",
    },
    detail: {
      // Items section heading — flips between comics and releases lists
      seriesInList: "Series in list",
      releasesInList: "Releases in list",
      empty: "This list is empty.",
      // Toolbar share/clipboard
      shareCopied: "Link copied to clipboard",
      shareError: "Couldn't copy the link. Try again.",
      // Visibility pill labels (rendered next to the title in edit mode)
      pills: {
        public: "Public",
        private: "Private",
      },
      row: {
        comicSubLabel: "Comic",
        releaseSubLabel: "Release",
        free: "Free",
        gold: "Gold",
        moreAria: "More options",
        removeButtonAria: 'Remove "{{name}}" from list',
        menuDetails: "Release details",
        // Detail-item kebab uses the same menu component as the
        // series-detail release card (ReleaseCardMenu) for both comic-
        // context and release-context rows. This override label is what
        // we hand it on comic rows so the entry reads "Comic details"
        // instead of the release-flavoured default.
        menuComicDetails: "Comic details",
        menuRead: "Continue reading",
        menuShare: "Share",
        shareCopied: "Link copied to clipboard",
        shareError: "Couldn't copy the link. Try again.",
        favoriteToast: "Added to your library",
        favoriteErrorToast: "Couldn't add to library",
        // Remove + undo
        removedToast: 'Removed "{{name}}" from list',
        removeErrorToast: "Couldn't remove from list. Try again.",
        removeUndoneToast: 'Restored "{{name}}"',
        removeUndoErrorToast: "Couldn't undo. Try removing it again later.",
        undoLabel: "Undo",
        // Remove confirmation dialog
        removeConfirmTitle: "Remove from list?",
        removeConfirmBody: 'Remove "{{name}}" from this list?',
        removeConfirmConfirm: "Remove",
        removeConfirmCancel: "Cancel",
      },
      sort: {
        popular: "Most popular",
        listOrder: "List order",
        recent: "Recently added",
      },
      editor: {
        // Top-toolbar — labeled "Edit list" button + secondary icon buttons
        editButton: "Edit list",
        unsavedChanges: "Unsaved changes",
        cancelButton: "Cancel",
        saveButton: "Save",
        deleteListAria: "Delete list",
        shareListAria: "Share list",
        // Toasts (used by useReadingListEdit hooks)
        saveSuccess: "List updated",
        saveError: "Couldn't save list. Try again.",
        visibilityError: "Couldn't update visibility. Try again.",
        nowPublic: "List is now public",
        nowPrivate: "List is now private",
        // Add comic picker
        addComicShort: "Add comic",
        addReleaseShort: "Add release",
        addItemError: "Couldn't add to list. Try again.",
        removeItemError: "Couldn't remove from list. Try again.",
        // Drag-to-reorder
        reorderHandleAria: "Drag to reorder",
        reorderError: "Couldn't save the new order. Try again.",
        positionInputAria:
          "Position {{position}} of {{total}}. Type a new number to move this item.",
        editModal: {
          title: "Edit list",
          closeAria: "Close",
          nameLabel: "Name",
          namePlaceholder: "e.g. The Vertigo Canon",
          nameRequired: "Required",
          nameCounter: "{{count}} / {{max}}",
          descriptionLabel: "Description",
          descriptionPlaceholder:
            "What's the mood, the angle, the pick order? This shows up on the list page.",
          descriptionOptional: "Optional",
          visibilityPublicLabel: "Public",
          visibilityPublicHelper:
            "Anyone on GlobalComix can view and save this list",
          visibilityPrivateLabel: "Private",
          visibilityPrivateHelper: "Only you can see this list",
          cancel: "Cancel",
          save: "Save",
          saveError: "Couldn't save list. Try again.",
        },
      },
      stats: {
        // Cells: views · favorites · series/releases · audience
        views: "views",
        favorites: "favorites",
        series: "series",
        releases: "releases",
        audience: "audience",
        // Display labels for `list.audience_rating` — the most-mature
        // audience facet across the list's items, stamped by the detail
        // endpoint. "All" is the default when nothing in the list
        // carries an audience facet; "13+" / "17+" map to the
        // "Teenagers" / "Adults" comic facet option names.
        audienceAll: "All",
        audienceTeen: "13+",
        audienceAdult: "17+",
      },
    },
    wizard: {
      title: "Create reading list",
      closeAria: "Close",
      progress: "{{current}}/{{total}}",
      back: "Back",
      skip: "Skip",
      next: "Next",
      create: "Create list",
      listType: {
        label: "List type",
        comicsLabel: "Comics",
        comicsHelper: "Collect whole comic series",
        releasesLabel: "Releases",
        releasesHelper: "Collect individual issues or chapters",
      },
      step1: {
        heading: "Name your list",
        description: "Short and memorable is best. You can change it later.",
        placeholder: "e.g. The Vertigo Canon",
        required: "Required",
        counter: "{{count}} / {{max}}",
      },
      step2: {
        heading: "Describe your list",
        description:
          "What's the mood, the angle, the pick order? This shows up on the list page.",
        placeholder:
          "A personal crawl through Vertigo's high-water marks…",
        optional: "Optional",
      },
      step3: {
        comicsHeading: "Add a few comics",
        comicsDescription:
          "Start with 2–3. You can always add more from the list page.",
        releasesHeading: "Add a few chapters",
        releasesDescription:
          "Start with 2–3. You can always add more from the list page.",
        privacyOnLabel: "Public",
        privacyOnHelper: "Anyone on GlobalComix can view and save this list",
        privacyOffLabel: "Private",
        privacyOffHelper: "Only you can see this list",
      },
      toasts: {
        created: 'List "{{name}}" created',
        createdAddPartial:
          'List "{{name}}" created, but {{failedCount}} of {{totalCount}} comics couldn\'t be added',
        createError: "Couldn't create list",
      },
    },
    picker: {
      // Modal/Drawer titles
      comicsTitle: "Add comics",
      releasesTitle: "Add chapters",
      // Search input
      comicsSearchPlaceholder: "Search comics…",
      releasesSearchPlaceholder: "Search chapters…",
      searchClearAria: "Clear search",
      // Section headings (only render in default-feed mode)
      alreadyInListHeading: "Already in this list",
      defaultFeedHeading: "Featured",
      searchResultsHeading: "Search results",
      // Empty + loading
      noResultsTitle: 'No matches for "{{term}}".',
      noResultsHint: "Try a different keyword or remove your filter.",
      loadMoreLoading: "Loading more…",
      feedErrorTitle: "Couldn't load comics. Try again.",
      // Card a11y
      addedCheckAria: "Already added",
      // Picked-strip — pinned row of items the user has already selected,
      // rendered above the search grid by PickedStrip.tsx.
      pickedHeading: "Your picks",
      removePickedAria: 'Remove "{{title}}" from picks',
      // Footer
      doneButton: "Done",
      // Footer + save flow (edit-mode batched commit)
      saveButton: "Save",
      discardButton: "Discard",
      saveChanges_one: "Save 1 change",
      saveChanges_other: "Save {{count}} changes",
      saveSuccess: "List updated",
      savePartialError_one: "1 change couldn't save. Try again.",
      savePartialError_other:
        "{{count}} changes couldn't save. Try again.",
      // Dirty-close confirm
      dirtyCloseTitle: "Discard changes?",
      dirtyCloseBody_one:
        "You have 1 unsaved change. It'll be lost if you close now.",
      dirtyCloseBody_other:
        "You have {{count}} unsaved changes. They'll be lost if you close now.",
      dirtyCloseConfirm: "Discard",
      dirtyCloseCancel: "Keep editing",
    },
  },
};
